import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { ScanBatch } from '../../types/scan';
import { FromApplicationContext } from '../../types/application';
import { SeverityPills } from '../findings/severityDisplay';
import ScanBranchCell from './ScanBranchCell';
import { SCAN_TYPE_NAMES, formatDateCompact } from './scanBatchDisplay';
import { prTerm } from '../../config/providers';

interface ScanBatchRowProps {
  batch: ScanBatch;
  applicationName: string;
  fromApplication: FromApplicationContext;
  /** Show the repository name under the scan type — only useful when the list spans applications. */
  showApplicationName?: boolean;
}

/**
 * One completed scan batch as a table row: scan type, findings by severity,
 * branch, start time, and a link through to the findings it surfaced.
 */
const ScanBatchRow: React.FC<ScanBatchRowProps> = ({
  batch,
  applicationName,
  fromApplication,
  showApplicationName = false,
}) => {
  const prLabel = prTerm(batch.metadata?.provider);
  const scanType = SCAN_TYPE_NAMES[batch.scan_type] || 'Security Scan';
  const scanMode = batch.metadata?.scan_mode;

  // Completed scans link straight to the findings they surfaced, filtered to this batch.
  const linkUrl = `/repositories/${fromApplication.appId}/findings?scan_batch_id=${batch.id}`;

  return (
    <tr className="hover:bg-ui-background-tertiary/40 transition-colors">
      <td className="py-3.5 px-4 align-middle">
        <Link to={linkUrl} className="font-semibold text-ui-text-primary hover:text-brand-primary transition-colors">
          {scanType}
        </Link>
        {showApplicationName && <div className="text-xs text-ui-text-tertiary mt-0.5">{applicationName}</div>}
        {(batch.metadata?.pr_number || scanMode === 'partial' || scanMode === 'full') && (
          <div className="flex items-center gap-2 mt-1 text-xs text-ui-text-tertiary">
            {batch.metadata?.pr_number &&
              (batch.metadata.pr_url ? (
                <a
                  href={batch.metadata.pr_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary hover:underline"
                >
                  {prLabel} #{batch.metadata.pr_number}
                </a>
              ) : (
                <span>
                  {prLabel} #{batch.metadata.pr_number}
                </span>
              ))}
            {(scanMode === 'partial' || scanMode === 'full') && (
              <span
                title={
                  scanMode === 'partial'
                    ? 'Partial scan — only files changed in this PR/MR were scanned'
                    : 'Full scan — entire repository was scanned'
                }
                className="px-1.5 py-0.5 rounded bg-ui-background-tertiary text-ui-text-tertiary"
              >
                {scanMode === 'partial' ? 'Partial' : 'Full'}
              </span>
            )}
          </div>
        )}
        {batch.scans_failed > 0 && (
          <div className="mt-1 flex items-start gap-1.5 text-xs text-semantic-error">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span className="line-clamp-2">
              {batch.scans_failed} failed
              {batch.error_message ? ` — ${batch.error_message}` : ''}
            </span>
          </div>
        )}
      </td>

      <td className="py-3.5 px-4 align-middle">
        <SeverityPills summary={batch.severity_breakdown ?? {}} emptyLabel="No findings" />
      </td>

      <ScanBranchCell branches={batch.branches} />

      <td className="w-px whitespace-nowrap py-3.5 px-4 align-middle text-sm text-ui-text-secondary">
        {formatDateCompact(batch.created_at)}
      </td>

      <td className="w-px whitespace-nowrap py-3.5 px-4 align-middle text-right">
        <Link
          to={linkUrl}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-primary hover:bg-brand-primaryDark text-black text-sm font-semibold transition-colors whitespace-nowrap"
        >
          Review
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </td>
    </tr>
  );
};

export default ScanBatchRow;
